import type { ComponentType } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CreditCard, Code, Shield, Lock, Share2, KeyRound, Cog, Link2, Palette, Menu } from 'lucide-react';

import { ProviderLayout } from '@/components/layouts/provider-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StripeConfiguration } from '@/components/billing/StripeConfiguration';
import { SocialOAuthConfiguration } from '@/components/settings/SocialOAuthConfiguration';
import { ProviderApiAccessTab } from '@/components/settings/ProviderApiAccessTab';
import { ProviderPasswordLoginCard } from '@/components/settings/ProviderPasswordLoginCard';
import { ProviderWebhooksCard } from '@/components/settings/ProviderWebhooksCard';
import { ProviderExternalPlanSelectionCard } from '@/components/settings/ProviderExternalPlanSelectionCard';
import { ProviderTermsSettingsCard } from '@/components/settings/ProviderTermsSettingsCard';
import { ProviderCheckoutContentCard } from '@/components/settings/ProviderCheckoutContentCard';
import { ProviderCheckoutFieldsCard } from '@/components/settings/ProviderCheckoutFieldsCard';
import { ProviderBrandingSettingsCard } from '@/components/settings/ProviderBrandingSettingsCard';
import { ProviderNotificationSettingsCard } from '@/components/settings/ProviderNotificationSettingsCard';
import { ProviderCheckoutDomainSettingsCard } from '@/components/settings/ProviderCheckoutDomainSettingsCard';
import { ProviderCustomScriptsCard } from '@/components/settings/ProviderCustomScriptsCard';
import { ProviderCustomerPortalMenuCard } from '@/components/settings/ProviderCustomerPortalMenuCard';
import { SignupRedirectSettings } from '@/components/settings/SignupRedirectSettings';
import { useAuth } from '@/hooks/useAuth';

interface SettingsSection {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: ComponentType<{ className?: string }>;
  badgeKey?: string;
}

const settingsSections: SettingsSection[] = [
  {
    id: 'general',
    titleKey: 'settings.sections.general.title',
    descriptionKey: 'settings.sections.general.description',
    icon: Cog,
  },
  {
    id: 'customer-portal-menu',
    titleKey: 'settings.sections.portalMenu.title',
    descriptionKey: 'settings.sections.portalMenu.description',
    icon: Menu,
  },
  {
    id: 'checkout',
    titleKey: 'settings.sections.checkout.title',
    descriptionKey: 'settings.sections.checkout.description',
    icon: CreditCard,
  },
  {
    id: 'branding',
    titleKey: 'settings.sections.branding.title',
    descriptionKey: 'settings.sections.branding.description',
    icon: Palette,
  },
  {
    id: 'stripe',
    titleKey: 'settings.sections.stripe.title',
    descriptionKey: 'settings.sections.stripe.description',
    icon: CreditCard,
  },
  {
    id: 'social-auth',
    titleKey: 'settings.sections.socialAuth.title',
    descriptionKey: 'settings.sections.socialAuth.description',
    icon: Shield,
  },
  {
    id: 'signup-redirect',
    titleKey: 'settings.sections.signupRedirect.title',
    descriptionKey: 'settings.sections.signupRedirect.description',
    icon: Link2,
  },
  {
    id: 'provider-password',
    titleKey: 'settings.sections.providerPassword.title',
    descriptionKey: 'settings.sections.providerPassword.description',
    icon: KeyRound,
  },
  {
    id: 'customer-password',
    titleKey: 'settings.sections.customerPassword.title',
    descriptionKey: 'settings.sections.customerPassword.description',
    icon: Lock,
  },
  {
    id: 'integrations',
    titleKey: 'settings.sections.integrations.title',
    descriptionKey: 'settings.sections.integrations.description',
    icon: Share2,
  },
  {
    id: 'api-access',
    titleKey: 'settings.sections.apiAccess.title',
    descriptionKey: 'settings.sections.apiAccess.description',
    icon: Code,
  },
];

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const userRoles = user?.roles ?? [];
  const isApiManager = useMemo(() => {
    if (!user) {
      return false;
    }

    const normalizedRoles = userRoles.map((role) => role.toLowerCase());
    const directRole = user.role?.toLowerCase();

    const privilegedRoles = ['provider_admin', 'provider_owner', 'owner', 'admin'];
    if (normalizedRoles.some((role) => privilegedRoles.includes(role))) {
      return true;
    }
    if (directRole && directRole !== 'customer') {
      return true;
    }

    // Fallback: if no explicit role metadata is present, treat provider users as admins.
    if (!directRole && normalizedRoles.length === 0) {
      return true;
    }

    return false;
  }, [user, userRoles]);

  const [activeTab, setActiveTab] = useState<string>('general');

  const translatedSections = useMemo<
    Array<SettingsSection & { title: string; description: string; badge?: string }>
  >(
    () =>
      settingsSections.map((section) => ({
        ...section,
        title: t(section.titleKey),
        description: t(section.descriptionKey),
        badge: section.badgeKey ? t(section.badgeKey) : undefined,
      })),
    [t],
  );

  return (
    <ProviderLayout>
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">{t('settings.title')}</h1>
          <p className="mt-2 text-muted-foreground">{t('settings.subtitle')}</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('settings.categoriesTitle')}</CardTitle>
              <CardDescription>{t('settings.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              {translatedSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeTab === section.id;
                const isRestricted = section.id === 'api-access' && !isApiManager;
                const isDisabled = Boolean(section.badgeKey) || isRestricted;

                return (
                  <button
                    key={section.id}
                    onClick={() => {
                      if (isRestricted) {
                        toast.error(
                          t(
                            'settings.providerApi.restricted',
                            'Only provider admins can manage API access.',
                          ),
                        );
                        return;
                      }
                      if (!section.badgeKey) {
                        setActiveTab(section.id);
                      }
                    }}
                    className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-left transition-all ${
                      isActive
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-transparent bg-muted hover:bg-muted/80'
                    } ${isDisabled ? 'cursor-not-allowed opacity-70' : ''}`}
                    disabled={isDisabled}
                  >
                    <Icon className="h-4 w-4" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium leading-tight">{section.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {section.description}
                      </span>
                    </div>
                    <div className="ml-2 flex items-center gap-2 self-start">
                      {section.badge && (
                        <Badge variant="secondary" className="text-xs">
                          {section.badge}
                        </Badge>
                      )}
                      {isRestricted && (
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Lock className="h-3 w-3" />
                          {t('settings.badges.restricted', 'Restricted')}
                        </Badge>
                      )}
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          <div className="space-y-6">
            {activeTab === 'stripe' && <StripeConfiguration />}

            {activeTab === 'social-auth' && <SocialOAuthConfiguration />}

            {activeTab === 'provider-password' && (
              <ProviderPasswordLoginCard scope="provider" />
            )}

            {activeTab === 'customer-password' && (
              <ProviderPasswordLoginCard scope="customer" />
            )}

            {activeTab === 'signup-redirect' && <SignupRedirectSettings />}

            {activeTab === 'integrations' && (
              <div className="space-y-6">
                <ProviderExternalPlanSelectionCard />
                <ProviderWebhooksCard />
              </div>
            )}

            {activeTab === 'general' && (
              <div className="space-y-6">
                <ProviderCustomScriptsCard />
                <ProviderNotificationSettingsCard />
              </div>
            )}

            {activeTab === 'customer-portal-menu' && <ProviderCustomerPortalMenuCard />}

            {activeTab === 'checkout' && (
              <div className="space-y-6">
                <ProviderCheckoutContentCard />
                <ProviderCheckoutFieldsCard />
                <ProviderCheckoutDomainSettingsCard />
                <ProviderTermsSettingsCard />
              </div>
            )}

            {activeTab === 'branding' && <ProviderBrandingSettingsCard />}

            {activeTab === 'api-access' && <ProviderApiAccessTab />}

          </div>
        </div>
      </div>
    </ProviderLayout>
  );
}
